import { apiService } from "../services/apiService";
import { useParams, useNavigate } from "react-router-dom";
import React, { useEffect, useState, useRef } from "react";
import { useAuth } from "../contexts/AuthContext";
import io from "socket.io-client";
import "animate.css";
import config from "../config";

const PublicQueue = () => {
    const navigate = useNavigate();
    const { club_id } = useParams();
    const { user, isAuthenticated } = useAuth();

    const [queues, setQueues] = useState([]);
    const [loading, setLoading] = useState(true);
    const [animatedItems, setAnimatedItems] = useState({});
    const [removedItems, setRemovedItems] = useState([]);
    const prevQueuesRef = useRef([]);

    const [pageIndex, setPageIndex] = useState({});
    const [visibleQueues, setVisibleQueues] = useState({});

    const [settings, setSettings] = useState({});

    const [tablePageIndex, setTablePageIndex] = useState(0);

    const fetchQueues = async () => {
        try {
            const response = await apiService.get(`/public/tables/queues/${club_id}`);
            const filtered = response.tables.filter(table =>
                table.table_type === 'snooker' && table.queues && table.queues.length > 0
            );
            setQueues([...filtered]);
        } catch (e) {
            console.error('Failed to fetch queues:', e);
            alert('Failed to load queue data');
        } finally {
            setLoading(false);
        }
    };

    const fetchSettings = async () => {
        try {
            const data = await apiService.get(`/settings/queue/${club_id || ""}`);
            setSettings(data.settings || {});
        } catch (e) {
            console.error('Failed to fetch settings:', e);
        }
    };

    const detectQueueChanges = (newQueues) => {
        const prevQueues = prevQueuesRef.current;
        const newAnimatedItems = {};
        const newRemovedItems = [];

        const flatten = (tables) =>
            tables.flatMap((table) =>
                (table.queues || []).map((q) => ({
                    id: q.id,
                    table_id: table.table_id,
                    player_name: q.player_name,
                    position: q.position,
                }))
            );

        const prevItems = flatten(prevQueues);
        const newItems = flatten(newQueues);

        newItems.forEach((n) => {
            const p = prevItems.find((i) => i.id === n.id);
            if (!p) newAnimatedItems[n.id] = "animate__fadeIn";
            else if (p.player_name !== n.player_name) newAnimatedItems[n.id] = "animate__fadeIn";
            else if (p.position !== n.position)
                newAnimatedItems[n.id] = p.position > n.position ? "animate__slideInUp" : "animate__slideInDown";
        });

        prevItems.forEach((p) => {
            if (!newItems.find((i) => i.id === p.id)) {
                newRemovedItems.push({ ...p, animation: "animate__fadeOut" });
            }
        });

        setAnimatedItems(newAnimatedItems);
        setRemovedItems(newRemovedItems);

        setTimeout(() => {
            setAnimatedItems({});
            setRemovedItems([]);
        }, 900);

        prevQueuesRef.current = JSON.parse(JSON.stringify(newQueues));
    };

    useEffect(() => {
        fetchQueues();
        fetchSettings();

        const socket = io(config.SOCKET_URL, { transports: ['polling', 'websocket'] });
        socket.on('connect', () => {
            console.log('Socket connected:', socket.id);
            socket.emit('joinClubQueue', club_id);
        });

        const handleTables = ({ clubId, tables }) => {
            if (String(clubId) !== String(club_id)) return;
            if (Array.isArray(tables) && tables.length > 0) {
                const filtered = tables.filter(t =>
                    t.table_type === 'snooker' && t.queues && t.queues.length > 0
                );
                detectQueueChanges(filtered);
                setQueues([...filtered]);
            } else {
                fetchQueues();
            }
        };

        socket.on('initialQueueState', handleTables);
        socket.on('queueUpdate', handleTables);
        socket.on('connect_error', (error) => {
            console.error('Socket connection error:', error.message);
            fetchQueues();
        });

        const pollInterval = setInterval(fetchQueues, 30000);

        return () => {
            socket.disconnect();
            clearInterval(pollInterval);
        };
    }, [club_id]);

    const PAGE_SIZE = Number(settings.queue_players_per_table) || 6;
    const ROTATE_MS = (Number(settings.queue_player_animation_duration) || 2) * 1000;

    useEffect(() => {
        setPageIndex((prev) => {
            const next = { ...prev };
            const seen = new Set();
            queues.forEach((t) => {
                const pages = Math.max(1, Math.ceil((t.queues?.length || 0) / PAGE_SIZE));
                const cur = prev[t.table_id] ?? 0;
                next[t.table_id] = cur % pages;
                seen.add(String(t.table_id));
            });
            Object.keys(next).forEach((k) => {
                if (!seen.has(String(k))) delete next[k];
            });
            return next;
        });
    }, [queues, PAGE_SIZE]);

    useEffect(() => {
        const interval = setInterval(() => {
            setPageIndex((prev) => {
                const next = { ...prev };
                queues.forEach((t) => {
                    const total = t.queues?.length || 0;
                    if (total > PAGE_SIZE) {
                        const pages = Math.ceil(total / PAGE_SIZE);
                        const cur = prev[t.table_id] || 0;
                        next[t.table_id] = (cur + 1) % pages;
                    } else {
                        next[t.table_id] = 0;
                    }
                });
                return next;
            });
        }, ROTATE_MS);

        return () => clearInterval(interval);
    }, [queues, PAGE_SIZE, ROTATE_MS]);

    useEffect(() => {
        const vis = {};
        queues.forEach((t) => {
            const index = pageIndex[t.table_id] || 0;
            const start = index * PAGE_SIZE;
            vis[t.table_id] = (t.queues || []).slice(start, start + PAGE_SIZE);
        });
        setVisibleQueues(vis);
    }, [queues, pageIndex, PAGE_SIZE]);

    const TABLES_PER_PAGE = Number(settings.queue_tables_to_display) || 2;
    const TABLE_ROTATE_MS = (Number(settings.queue_table_animation_duration) || 3) * 1000;

    useEffect(() => {
        const interval = setInterval(() => {
            if (queues.length > TABLES_PER_PAGE) {
                setTablePageIndex((prev) => prev + 1);
            }
        }, TABLE_ROTATE_MS);

        return () => clearInterval(interval);
    }, [queues.length, TABLES_PER_PAGE, TABLE_ROTATE_MS]);

    useEffect(() => {
        if (!isAuthenticated()) {
            navigate('/login');
        }
    }, [isAuthenticated, navigate]);

    const css = `
        :root {
            --u: 1vmin;
            --bg: #0f1113;
            --panel: #14171a;
            --panel-2: #1a1e22;
            --text: #e6e7e8;
            --muted: #9aa0a6;
            --line: #22262a;
            --accent: #7aa2ff;
            --radius-lg: 1.6vmin;
            --radius-md: 1vmin;
            --head-h: 8vmin;
            --foot-h: 7vmin;
            --queue-item-h: 6.2vmin;
            --queue-item-gap: 1.2vmin;
            --padding: 2.2vmin;
        }
        @keyframes spin { to { transform: rotate(360deg) } }
        @keyframes slideInUpImproved {
            from { opacity: 0; transform: translate3d(0, 3vmin, 0) }
            to { opacity: 1; transform: translate3d(0, 0, 0) }
        }
        @keyframes slideInDownImproved {
            from { opacity: 0; transform: translate3d(0, -3vmin, 0) }
            to { opacity: 1; transform: translate3d(0, 0, 0) }
        }
        @keyframes pageSlideIn {
            from { opacity: 0; transform: translateX(4vmin) }
            to { opacity: 1; transform: translateX(0) }
        }
        .pageSwap {
            animation: pageSlideIn .35s ease-out;
            will-change: transform, opacity;
        }
        .animate__slideInUp { animation: slideInUpImproved 0.4s ease-out }
        .animate__slideInDown { animation: slideInDownImproved 0.4s ease-out }
    `;

    const positionColors = ["#dfe1e2", "#b9bec3", "#8b9197", "#6a7076", "#4f555b", "#3b4046"];

    if (loading) {
        return (
            <div style={{
                minHeight: "100vh",
                background: "var(--bg)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
            }}>
                <style>{css}</style>
                <div style={{ textAlign: "center" }}>
                    <div style={{
                        width: "6vmin",
                        height: "6vmin",
                        margin: "0 auto 2vmin",
                        border: "0.4vmin solid #2a2e33",
                        borderTop: "0.4vmin solid #47505a",
                        borderRadius: "50%",
                        animation: "spin 1s linear infinite",
                    }} />
                    <h2 style={{
                        color: "var(--text)",
                        fontSize: "2.6vmin",
                        fontWeight: 600,
                        letterSpacing: "0.1vmin",
                    }}>
                        Loading queue...
                    </h2>
                </div>
            </div>
        );
    }

    if (!isAuthenticated()) {
        return null;
    }

    const tablePages = Math.max(1, Math.ceil(queues.length / TABLES_PER_PAGE));
    const currentTablePage = tablePageIndex % tablePages;
    const tableStart = currentTablePage * TABLES_PER_PAGE;
    const visibleTables = queues.slice(tableStart, tableStart + TABLES_PER_PAGE);

    return (
        <div style={{ minHeight: "100vh", background: "var(--bg)", color: "var(--text)" }}>
            <style>{css}</style>

            <div style={{ maxWidth: "160vmin", margin: "0 auto", padding: "6vmin 4vmin" }}>
                <div className="animate__animated animate__fadeInDown" style={{ textAlign: "center", marginBottom: "5vmin" }}>
                    <h1 style={{ fontSize: "4.6vmin", margin: 0, fontWeight: 700, letterSpacing: "0.2vmin" }}>
                        Table Queue
                    </h1>
                    <div style={{
                        marginTop: "1.6vmin",
                        display: "inline-block",
                        color: "var(--muted)",
                        fontSize: "2vmin",
                        padding: "1vmin 1.6vmin",
                        border: "0.15vmin solid var(--line)",
                        borderRadius: "0.8vmin",
                    }}>
                        Club {club_id} • Live
                    </div>
                </div>

                {queues.length > 0 || removedItems.length > 0 ? (
                    <>
                        <div key={`table-page-${currentTablePage}`} className="pageSwap" style={{
                            display: "grid",
                            gridTemplateColumns: "repeat(auto-fit, minmax(46vmin, 1fr))",
                            gap: "3vmin",
                        }}>
                            {visibleTables.map((table) => {
                                const slice = visibleQueues[table.table_id] || [];
                                const total = table.queues?.length || 0;
                                const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));
                                const curPage = pageIndex[table.table_id] || 0;
                                const displayCount = Math.min(PAGE_SIZE, slice.length + removedItems.filter((it) => it.table_id === table.table_id).length);
                                const cardHeight = `calc(var(--head-h) + var(--foot-h) + (${displayCount} * (var(--queue-item-h) + var(--queue-item-gap)) + 2 * var(--padding)))`;

                                return (
                                    <div key={table.table_id}>
                                        <div
                                            className="animate__animated animate__fadeInUp"
                                            style={{
                                                background: "var(--panel)",
                                                borderRadius: "var(--radius-lg)",
                                                border: "0.15vmin solid var(--line)",
                                                overflow: "hidden",
                                                height: cardHeight,
                                                display: "flex",
                                                flexDirection: "column",
                                            }}
                                        >
                                            <div style={{
                                                background: "var(--panel-2)",
                                                padding: "2vmin",
                                                borderBottom: "0.15vmin solid var(--line)",
                                                height: "var(--head-h)",
                                                display: "flex",
                                                alignItems: "center",
                                            }}>
                                                <h3 style={{ margin: 0, fontSize: "2.4vmin", fontWeight: 600, color: "var(--text)" }}>
                                                    {table.table_name}
                                                </h3>
                                            </div>

                                            <div style={{
                                                padding: "var(--padding)",
                                                flex: 1,
                                                overflow: "hidden",
                                                position: "relative",
                                                display: "block",
                                            }}>
                                                <div key={`${table.table_id}-${curPage}`} className="pageSwap" style={{ height: "100%" }}>
                                                    <div style={{ display: "grid", gap: "var(--queue-item-gap)" }}>
                                                        {slice.map((queue, qi) => (
                                                            <div
                                                                key={queue.id}
                                                                className={`animate__animated ${animatedItems[queue.id] || ""}`}
                                                                style={{
                                                                    display: "flex",
                                                                    alignItems: "center",
                                                                    gap: "1.4vmin",
                                                                    padding: "1.4vmin 1.6vmin",
                                                                    background: "#181b1f",
                                                                    border: "0.15vmin solid var(--line)",
                                                                    borderRadius: "var(--radius-md)",
                                                                    height: "var(--queue-item-h)",
                                                                }}
                                                            >
                                                                <div style={{
                                                                    width: "3.4vmin",
                                                                    height: "3.4vmin",
                                                                    borderRadius: "50%",
                                                                    background: positionColors[qi % positionColors.length],
                                                                    color: "#0f1113",
                                                                    display: "flex",
                                                                    alignItems: "center",
                                                                    justifyContent: "center",
                                                                    fontSize: "1.8vmin",
                                                                    fontWeight: 700,
                                                                    border: queue.position === 1
                                                                        ? "0.25vmin solid var(--accent)"
                                                                        : "0.25vmin solid transparent",
                                                                }}>
                                                                    {queue.position}
                                                                </div>

                                                                <div style={{
                                                                    flex: 1,
                                                                    fontSize: "2vmin",
                                                                    color: "var(--text)",
                                                                    fontWeight: 500,
                                                                    letterSpacing: "0.02vmin",
                                                                    display: "flex",
                                                                    alignItems: "center",
                                                                    marginLeft: "1.6vmin"
                                                                }}>
                                                                    {queue.player_name}
                                                                    {queue.position === 1 && (
                                                                        <span
                                                                            aria-label="current"
                                                                            title="Current"
                                                                            style={{
                                                                                display: "inline-block",
                                                                                width: "0.8vmin",
                                                                                height: "0.8vmin",
                                                                                borderRadius: "50%",
                                                                                background: "var(--accent)",
                                                                                marginLeft: "1vmin"
                                                                            }}
                                                                        />
                                                                    )}
                                                                </div>
                                                            </div>
                                                        ))}

                                                        {removedItems
                                                            .filter((it) => it.table_id === table.table_id)
                                                            .map((it) => (
                                                                <div
                                                                    key={it.id}
                                                                    className={`animate__animated ${it.animation}`}
                                                                    style={{
                                                                        display: "flex",
                                                                        alignItems: "center",
                                                                        gap: "1.4vmin",
                                                                        padding: "1.4vmin 1.6vmin",
                                                                        background: "#15181b",
                                                                        border: "0.15vmin dashed var(--line)",
                                                                        borderRadius: "var(--radius-md)",
                                                                        color: "var(--muted)",
                                                                        textDecoration: "line-through",
                                                                        height: "var(--queue-item-h)",
                                                                    }}
                                                                >
                                                                    <div style={{
                                                                        width: "3.4vmin",
                                                                        height: "3.4vmin",
                                                                        borderRadius: "50%",
                                                                        background: "#2a2e33",
                                                                        color: "#8a9096",
                                                                        display: "flex",
                                                                        alignItems: "center",
                                                                        justifyContent: "center",
                                                                        fontSize: "1.8vmin",
                                                                        fontWeight: 700,
                                                                    }}>
                                                                        {it.position}
                                                                    </div>
                                                                    <div style={{ fontSize: "2vmin", marginLeft: "1.6vmin" }}>
                                                                        {it.player_name}
                                                                    </div>
                                                                </div>
                                                            ))}
                                                    </div>
                                                </div>
                                            </div>

                                            <div style={{
                                                marginTop: "auto",
                                                padding: "1.8vmin 2.2vmin",
                                                borderTop: "0.15vmin solid var(--line)",
                                                height: "var(--foot-h)",
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: (pages > 1) ? "space-between" : "flex-end",
                                                gap: "2vmin",
                                                color: "var(--muted)",
                                            }}>
                                                {pages > 1 && (
                                                    <div style={{ display: "flex", gap: "0.8vmin" }} aria-label="pagination">
                                                        {Array.from({ length: pages }).map((_, i) => (
                                                            <span
                                                                key={i}
                                                                style={{
                                                                    width: "0.8vmin",
                                                                    height: "0.8vmin",
                                                                    borderRadius: "50%",
                                                                    background: i === curPage ? "var(--text)" : "#3a3f45",
                                                                    opacity: i === curPage ? 1 : 0.6,
                                                                }}
                                                            />
                                                        ))}
                                                    </div>
                                                )}
                                                <div style={{ fontSize: "1.8vmin", textAlign: "right" }}>
                                                    {total} player{total !== 1 ? "s" : ""} in queue
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                        {queues.length > TABLES_PER_PAGE && (
                            <div style={{ display: "flex", justifyContent: "center", alignItems: "center", marginTop: "3vmin", gap: "2vmin", color: "var(--muted)" }}>
                                <div style={{ display: "flex", gap: "0.8vmin" }} aria-label="table pagination">
                                    {Array.from({ length: tablePages }).map((_, i) => (
                                        <span
                                            key={i}
                                            style={{
                                                width: "0.8vmin",
                                                height: "0.8vmin",
                                                borderRadius: "50%",
                                                background: i === currentTablePage ? "var(--text)" : "#3a3f45",
                                                opacity: i === currentTablePage ? 1 : 0.6,
                                            }}
                                        />
                                    ))}
                                </div>
                                <div style={{ fontSize: "1.8vmin" }}>
                                    {queues.length} tables with queues
                                </div>
                            </div>
                        )}
                    </>
                ) : (
                    <div style={{ display: "grid", placeItems: "center", padding: "12vmin 2vmin" }}>
                        <div
                            className="animate__animated animate__fadeIn"
                            style={{
                                background: "var(--panel)",
                                border: "0.15vmin solid var(--line)",
                                borderRadius: "var(--radius-lg)",
                                padding: "6vmin",
                                textAlign: "center",
                                maxWidth: "80vmin",
                            }}
                        >
                            <h3 style={{ margin: 0, fontSize: "3vmin", fontWeight: 600 }}>All tables available</h3>
                            <div style={{ marginTop: "1.6vmin", color: "var(--muted)", fontSize: "2vmin" }}>
                                No queues active • Ready to play
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default PublicQueue;